<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import DateUtils from '@/utils/date-utils';
import ApplicationScoreCard from '@/components/Conversations/Application/ApplicationScoreCard.vue';

import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';

import {
  type ApplicationIndex,
  type ConversationIndex,
  SearchConversationOrderByFields,
} from '@/core/conversations/conversation-index/conversation-index.type';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import ProjectService from '@/core/shared/project/project.service';
import Checkbox from '@/components/Shared/Input/Checkbox/Checkbox.vue';
import DoNotContactChip from '@/components/Common/DoNotContactChip.vue';
import ApplicantOriginChip from '@/components/Common/ApplicantOriginChip.vue';
import { useRoute, useRouter } from 'vue-router';
import ApplicationViewService from '@/core/applications/application-view.service';
import { getCurrentCandidateTab } from '@/composables/jobs/candidateTabs';
import { withoutDeepLinkQuery } from '@/composables/jobs/deepLinkQuery';
import { isApplicationNotSourcedByAiRecruiter } from '@/core/conversations/application-score/application-score.util';

const props = defineProps<{
  conversationIndex: ConversationIndex;
}>();

const emit = defineEmits<{
  (e: 'onUpdateCheck', payload: boolean): void;
}>();

const router = useRouter();
const route = useRoute();

const projectService = new ProjectService();
const conversationIndexService = new ConversationIndexService();
const applicationViewService = new ApplicationViewService();

const jobTitle = ref<string>();

const isSelected = computed(() => {
  // Check if the route conversationId matches (for jobs page)
  if (route.params.conversationId) {
    return Number(route.params.conversationId) === props.conversationIndex.conversationId;
  }
  // Otherwise use service selection state (for conversations page)
  return (
    conversationIndexService.selectedConversationIndex?.conversationId ===
    props.conversationIndex.conversationId
  );
});

const lastEmploymentDescription = computed(() => {
  let employmentDescription = '';

  if (props.conversationIndex.lastProJobTitle) {
    employmentDescription += props.conversationIndex.lastProJobTitle;

    if (props.conversationIndex.lastProEmployerName) {
      employmentDescription += ` at ${props.conversationIndex.lastProEmployerName}`;
    }
  }

  return employmentDescription;
});

const isBulkSelection = computed(() => props.conversationIndex.isBulkSelection || false);

const cardDate = computed(() => {
  const isCurrentSortByApplicationDate =
    conversationIndexService.conversationIndexSearch.orderBy.field ===
    SearchConversationOrderByFields.APPLICATION_APPLY_TS;
  let cardDate = props.conversationIndex.lastMessageSentTs || props.conversationIndex.updateTs;

  if (
    isCurrentSortByApplicationDate &&
    props.conversationIndex.applicationIndexes?.[0]?.applicationApplyTs
  ) {
    cardDate = props.conversationIndex.applicationIndexes?.[0]?.applicationApplyTs;
  }

  return DateUtils.formatTimeAndDateIfNotToday(cardDate);
});

const showCopilotOutreach = computed(() => {
  return (
    props.conversationIndex.primaryApplicationIndex?.initiatedByCopilot &&
    props.conversationIndex.primaryApplicationIndex?.initiatedByOutreach
  );
});

const showManualSourced = computed(() =>
  isApplicationNotSourcedByAiRecruiter(
    props.conversationIndex.primaryApplicationIndex?.initiatedByOutreach,
    props.conversationIndex.primaryApplicationIndex?.initiatedByCopilot,
  ),
);

const isApplicationViewed = computed(() => {
  // Use primaryApplicationIndex as it represents the main application for this card
  if (!props.conversationIndex.primaryApplicationIndex?.applicationId) {
    return true; // No application, consider as viewed
  }

  // Cast partial to full ApplicationIndex for service (safe as we need minimal fields)
  return applicationViewService.isApplicationViewed(
    props.conversationIndex.primaryApplicationIndex as ApplicationIndex,
  );
});

const getProjectTitle = async (jobId?: number) => {
  if (!jobId) return '';
  return projectService.projectTitlesByJobId.get(jobId);
};

async function handleCardOnClick() {
  await Promise.all([trackApplicationView(), selectConversation(props.conversationIndex)]);
}

async function selectConversation(conversation: ConversationIndex) {
  const signal = conversationIndexService.beginCardSelection();
  try {
    const jobId = conversation.primaryApplicationIndex?.jobId;
    if (!jobId) {
      throw new Error('Conversation has no associated job');
    }

    const project = await projectService.getProjectByJobIdFromDeepLink(jobId, { signal });
    if (signal.aborted) return;
    if (!project) {
      throw new Error('Could not find project for this conversation');
    }

    const currentTab = getCurrentCandidateTab(route.path);

    // Keep the filter state, but not a pending deep link that would override this click.
    const currentQuery = withoutDeepLinkQuery(route.query);

    // Navigate to the new conversation URL. The `useJobsRouting` composable
    // is watching the route and will handle all the complex logic of fetching
    // data, updating the status, and positioning the conversation at the top.
    await router.push({
      path: `/jobs/${project.id}/candidates/${conversation.conversationId}/${currentTab}`,
      query: currentQuery,
    });

    TrackingService.trackAction(TrackingActionName.CONVERSATION_SELECTED, {
      job_ids: conversation.applicationIndexes?.map((application) => application.jobId),
      conversation_id: conversation.conversationId,
    });
  } catch (error) {
    if (signal.aborted) return;
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to load conversation information');
  }
}

async function trackApplicationView() {
  jobTitle.value = await getProjectTitle(props.conversationIndex.primaryApplicationIndex?.jobId);

  // Track view when card is displayed for jobs page
  if (!isApplicationViewed.value) {
    const applicationId = props.conversationIndex.primaryApplicationIndex?.applicationId;
    if (applicationId) {
      applicationViewService.markApplicationAsViewed(applicationId).catch(() => {
        // Silently handle errors - visual state handled by computed property
      });
    }
  }
}

onMounted(async () => {
  jobTitle.value = await getProjectTitle(props.conversationIndex.primaryApplicationIndex?.jobId);
  if (isSelected.value) {
    trackApplicationView();
  }
});
</script>

<template>
  <div>
    <div
      @click="handleCardOnClick"
      :id="`conversation-index-card-${conversationIndex.conversationId}`"
      :data-test="`conversation-index-card`"
      :data-test-conversation-id="conversationIndex.conversationId"
      :data-selected="isSelected"
      :class="isSelected ? 'bg-highlight-50' : 'group bg-[#fff] hover:bg-highlight-50'"
      class="relative flex cursor-pointer flex-col rounded-lg p-3"
    >
      <img
        v-if="isSelected"
        class="absolute bottom-0 left-0 top-0"
        src="@/assets/svg/conversations/selected-conversation-vertical-line.svg"
        alt="Selected"
        width="8"
      />
      <div class="flex h-6 w-full items-center justify-between">
        <div
          class="position-relative mr-1 flex w-[220px] items-center truncate text-sm font-bold text-shade-900"
        >
          <Checkbox
            @click.stop="emit('onUpdateCheck', !conversationIndex.isBulkSelection)"
            class="bulk-selection-checkbox mr-2"
            :class="{ '!ml-0': isBulkSelection }"
            :model-value="isBulkSelection"
          />
          <span
            class="sensitive truncate"
            :class="{
              'font-normal': isApplicationViewed,
              '!text-shade-840': isApplicationViewed && !isSelected,
              '!text-shade-860': isApplicationViewed && isSelected,
            }"
          >
            {{ conversationIndex.proName }}
          </span>
        </div>
        <div
          :class="{
            'whitespace-nowrap text-right text-xs': true,
            [isSelected ? 'text-shade-840' : 'text-shade-820']: !isApplicationViewed,
            'text-shade-820': isApplicationViewed && isSelected,
            'text-shade-800 group-hover:!text-shade-820': isApplicationViewed && !isSelected,
          }"
        >
          {{ cardDate }}
        </div>
      </div>

      <div class="flex h-6 w-full items-center justify-between">
        <div
          v-if="lastEmploymentDescription"
          class="w-[250px] truncate text-xs"
          :class="isSelected ? 'text-shade-820' : 'text-shade-800 group-hover:!text-shade-820'"
        >
          {{ lastEmploymentDescription }}
        </div>
        <div
          v-if="!conversationIndex.readStatus"
          class="h-4 w-4 rounded-full bg-highlight-500"
        ></div>
      </div>
      <div class="mt-1 flex flex-wrap items-center gap-1">
        <div class="min-w-0">
          <ApplicationScoreCard
            :job-title="jobTitle || ''"
            :score="conversationIndex.primaryApplicationIndex?.score"
            :is-selected="isSelected"
            :is-viewed="isApplicationViewed"
            variant="compact"
            leading-gutter
            bold
          />
        </div>
        <ApplicantOriginChip
          :integration-id="conversationIndex.primaryApplicationIndex?.integrationId"
          :is-ai-sourced="showCopilotOutreach"
          :is-sourced="showManualSourced"
          tall
          bold
          class="shrink-0"
        />
        <DoNotContactChip v-if="conversationIndex.doNotContact === true" />
      </div>
    </div>
  </div>
</template>
<style scoped>
.bulk-selection-checkbox {
  @apply ml-[-24px] inline-block transition-all duration-300;
}
div[id^='conversation-index-card'] {
  @apply transition-colors duration-300;

  &:hover {
    .bulk-selection-checkbox {
      @apply ml-0;
    }
  }
}
</style>
